/**
 * System Tenants Page
 * 
 * Admin-only page for managing all tenants in the platform.
 * Features:
 * - List all tenants with status, usage, and storage info
 * - Create new tenants
 * - View/edit tenant configuration
 */

import { useState } from 'react';
import { useSystemTenants } from '../hooks';
import { TenantConfigModal, CreateTenantModal } from '../components';
import { useI18n } from '@/i18n';
import {
  Button,
  Badge,
  Skeleton,
  Card,
} from '@/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { formatDate } from '@/lib/utils';
import { Plus, Settings, Building2 } from 'lucide-react';
import type { SystemTenant } from '../types';

// =============================================================================
// Status Badge Component
// =============================================================================

function StatusBadge({ status }: { status: SystemTenant['status'] }) {
  const { t } = useI18n();
  const variants: Record<SystemTenant['status'], 'default' | 'secondary' | 'destructive'> = {
    active: 'default',
    pending: 'secondary',
    suspended: 'destructive',
  };

  return (
    <Badge variant={variants[status]}>
      {t(`admin.system.tenants.status.${status}`)}
    </Badge>
  );
}

// =============================================================================
// Storage Cell Component
// =============================================================================

function StorageCell({ tenant }: { tenant: SystemTenant }) {
  const isCritical = tenant.storage_percent > 90;
  const isWarning = tenant.storage_percent > 75;

  return (
    <div className="min-w-[120px]">
      <div className="flex justify-between text-xs mb-1">
        <span className={isCritical ? 'text-destructive' : isWarning ? 'text-amber-600' : 'text-muted-foreground'}>
          {tenant.storage_percent.toFixed(0)}%
        </span>
        <span className="text-muted-foreground">
          {tenant.storage_used_gb.toFixed(1)} / {tenant.storage_quota_gb} GB
        </span>
      </div>
      <Progress
        value={tenant.storage_percent}
        className={`h-1.5 ${isCritical ? '[&>div]:bg-destructive' : isWarning ? '[&>div]:bg-amber-500' : ''}`}
      />
    </div>
  );
}

// =============================================================================
// Table Skeleton
// =============================================================================

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

// =============================================================================
// System Tenants Page
// =============================================================================

export function SystemTenantsPage() {
  const { t } = useI18n();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: tenantsResponse, isLoading, error } = useSystemTenants({
    skip: page * pageSize,
    limit: pageSize,
  });

  const tenants = tenantsResponse?.data ?? [];
  const total = tenantsResponse?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            System Tenants
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.system.tenants.description')}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.system.tenants.create')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="p-6 mb-6 border-destructive">
          <p className="text-destructive">
            Failed to load tenants: {error instanceof Error ? error.message : t('common.error')}
          </p>
        </Card>
      )}

      {/* Tenants Table */}
      <Card>
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : tenants.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('admin.system.tenants.noTenants')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('admin.system.tenants.noTenantsHint')}
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('admin.system.tenants.create')}
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.system.tenants.tableHeaders.name')}</TableHead>
                  <TableHead>{t('admin.system.tenants.tableHeaders.status')}</TableHead>
                  <TableHead className="text-center">{t('admin.system.tenants.tableHeaders.members')}</TableHead>
                  <TableHead className="text-center">{t('admin.system.tenants.tableHeaders.agents')}</TableHead>
                  <TableHead>{t('admin.system.tenants.tableHeaders.storage')}</TableHead>
                  <TableHead>{t('admin.system.tenants.tableHeaders.created')}</TableHead>
                  <TableHead className="text-right">{t('admin.system.tenants.tableHeaders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell>
                      <StatusBadge status={tenant.status} />
                    </TableCell>
                    <TableCell className="text-center">{tenant.member_count}</TableCell>
                    <TableCell className="text-center">{tenant.agent_count}</TableCell>
                    <TableCell>
                      <StorageCell tenant={tenant} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(tenant.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTenantId(tenant.id)}
                      >
                        <Settings className="h-4 w-4" />
                        <span className="sr-only">Configure</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modals */}
      {selectedTenantId && (
        <TenantConfigModal
          tenantId={selectedTenantId}
          onClose={() => setSelectedTenantId(null)}
        />
      )}

      {showCreateModal && (
        <CreateTenantModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}
